import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchFamilyById } from "../../service/FamilyApi";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Breadcrumbs, Loading } from "../../components/shared";
import { convertTimestampToDate, useCheckAccess, renderMemberTree, buildMemberHierarchy, maskAadhar } from "../../utils/Utilities";
import FamilySmartCard from './FamilySmartCard';
import { useSelector } from "react-redux";
import { getFamilyFileDownladUrl } from '../../service/FileApi';
import { statusExplanation } from '../general/DropDownValuesConfig';
import userIcon from "../../assets/pictures/user-icon.png";
import PropTypes from 'prop-types';
import { FaUserPlus, FaEdit, FaMapMarkerAlt, FaUsers, FaExchangeAlt } from "react-icons/fa";
// Correcting MemberPhoto logic to show photo next to the name in table
function MemberPhoto({ memberUid, loadedPhotoImages }) {
  // Find the photo URL for the member based on their UID
  const memberPhoto = loadedPhotoImages.find((img) => img.uid === memberUid);
  return memberPhoto ? (
    <img
      src={memberPhoto.url || userIcon}  // Use the URL from the API response
      alt={``}
      style={{

        width: '50px',       // Fixed width for passport size
        height: '60px',       // Maintain aspect ratio
        borderRadius: '25%',  // Slightly rounded corners
        objectFit: 'cover',   // Ensures proper crop if needed
        maxWidth: '100%',

      }}
    />
  ) : (
    <img
      src={userIcon}  // Use the URL from the API response
      alt={``}
      style={{

        width: '50px',       // Fixed width for passport size
        height: '60px',       // Maintain aspect ratio
        borderRadius: '25%',  // Slightly rounded corners
        objectFit: 'cover',   // Ensures proper crop if needed
        maxWidth: '100%',

      }}
    />
  );
}
function FamilyView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState(null);
  const [error, setError] = useState(null);

  useCheckAccess('FamilyRO'); // Check if the user is logged in (if no user data, redirect to login)
  const user = useSelector((state) => state.user);

  const [loadedPhotoImages, setLoadedPhotoImages] = useState([]);
  const [loadedApplicationImages, setLoadedApplicationImages] = useState([]);

  const [applicationForm, setApplicationForm] = useState(null); // To store the application form data
  const [loadingApplicationForm, setLoadingApplicationForm] = useState(false);

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        if (id) {
          const data = await fetchFamilyById(id); // Await the fetchFamilyById function
          setFamilyData(data); // Set the family data in state
        }
      } catch (error) {
        setError("Error fetching family data");
      }
    };

    fetchFamilyData(); // Call the async function inside useEffect
  }, [id]); // Only re-run if id changes

  if (error) {
    return <div>Error: {error}</div>; // Display error message
  }

  if (!familyData) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Load all family member photos only when button is clicked
  // Function to load member photos

  const loadAllMemberPhotos = async () => {
    try {
      // Extract all UIDs from the family members
      const memberUids = familyData.family.familyMembers.map(member => member.uid);


      const res = await getFamilyFileDownladUrl({
        category: 'family_member',
        subcategory: 'photo',
        familyMemberUids: memberUids, // Send all UIDs in a batch
      });

      if (res.status === 200) {
        const urls = res.data.results.map(member => {
          // Decide which URL to use (priority: signedUrl > publicUrl > path)
          const photoUrl = member.signedUrl || member.publicUrl || member.path;
          return { url: photoUrl, label: `${member.firstName} ${member.lastName}`, uid: member.uid };
        });
        setLoadedPhotoImages(urls);
      } else {
        console.error('Failed to load family member photos.');
      }
    } catch (err) {
      console.error("Failed to load family member photos.");
    }
  }



  // Load the latest application form only when button is clicked

  const loadLatestApplicationForm = async () => {
    setLoadingApplicationForm(true);
    setLoadedApplicationImages([]);
    try {
      const res = await getFamilyFileDownladUrl({
        category: "family",
        subcategory: "application_form",
        familyId: familyData.family.id,
      });

      setApplicationForm(res.data.applicationForm);
    } catch (err) {
      console.error("Failed to load latest application form photo.");
    } finally {
      setLoadingApplicationForm(false);
    }
  }


  // Function to load application forms (this can be used for loading all forms)

  const loadAllApplicationForms = async () => {
    setLoadingApplicationForm(true);
    setApplicationForm(null);
    try {

      const res = await getFamilyFileDownladUrl({
        category: "family",
        subcategory: "application_form",
        familyId: familyData.family.id,
        fetchFullAudit: true,
      });

      setLoadedApplicationImages(res.data.applicationFormList);
    } catch (err) {
      console.error("Failed to load application form photos.");
    } finally {
      setLoadingApplicationForm(false);
    }
  }

  const showPhotoColumn = familyData.family.familyMembers.some(member =>
    loadedPhotoImages.some(img => img.uid === member.uid)
  );

  return (
    <>
      <Navbar />
      <Helmet>
        <title>View Family</title>
        <link rel="canonical" href={`/family/view/${id}`} />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs
              customLinks={[
                { name: familyData?.family?.id || "", path: `/family/view/${id}` },
              ]}
            />
            <br />

            <h3>குடும்ப விவரங்கள்</h3>
            <br />
            <div className="smart-card-container">
              <FamilySmartCard familyData={familyData} printEnable={user?.roles?.includes('FamilyAdmin')} />
            </div>
            <br />
            <div className="details-card">
              <div className="detail-item">
                <span className="detail-label"><strong>குடும்ப எண் :</strong></span>
                <span className="detail-value">{familyData.family.id || ""}</span>
              </div>

              <div className="detail-item">
                <span className="detail-label"><strong>கூட்டம்:</strong></span>
                <span>{familyData.family.kootamName || ""}</span>
              </div>

              {familyData.family.customGroup && (
                <div className="detail-item">
                  <span className="detail-label"><strong>மற்ற குழு:</strong></span>
                  <span>{familyData.family.customGroup}</span>
                </div>
              )}

              <div className="detail-item">
                <span className="detail-label"><strong>குடும்பத் தலைவர் ஆதார் எண்:</strong></span>
                <span style={{ color: familyData.family.maskedFamilyHeadAadhar?.startsWith("TMP") ? "red" : "inherit" }}>
                  {maskAadhar(familyData.family.maskedFamilyHeadAadhar) || ""}
                </span>

              </div>
              <div className="detail-item">
                <span className="detail-label"><strong>குடும்பத் தலைவர்:</strong></span>
                <span>
                  {familyData.family.familyHeadFirstName || ""}
                  {familyData.family.familyHeadLastName ? ` ${familyData.family.familyHeadLastName}` : ""}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label"><strong>மொபைல் எண்:</strong></span>
                <span>
                  {familyData.family.familyHeadMobile}
                  {familyData.family.familyHeadMobile && familyData.family.familyHeadMobile2 ? " / " : ""}
                  {familyData.family.familyHeadMobile2}
                </span>
              </div>
              {/*
              <div className="detail-item">
                <span className="detail-label"><strong>நிரந்தர அஞ்சல்:</strong></span>
                <span>{familyData.family.permanentAddressName_tamil || ""}{", "}{familyData.family.permanentAddressBlock_tamil || ""}{", "}{familyData.family.permanentAddressDistrict_tamil || ""}</span>
              </div>
              {familyData.family.tempAddressDistrict_tamil && (
                <div className="detail-item">
                  <span className="detail-label"><strong>தற்காலிக அஞ்சல் :</strong></span>
                  <span>
                    {familyData.family.tempAddressName_tamil || ""}
                    {familyData.family.tempAddressBlock_tamil ? ", " + familyData.family.tempAddressBlock_tamil : ""}
                    {familyData.family.tempAddressDistrict_tamil ? ", " + familyData.family.tempAddressDistrict_tamil : ""}
                  </span>
                </div>
              )}*/}
              {familyData.family.applicationFormNumber && (
                <div className="detail-item">
                  <span className="detail-label"><strong>விண்ணப்ப எண்:</strong></span>
                  <span>{familyData.family.applicationFormNumber || ""}</span>
                </div>
              )}
              {familyData.family.familyName && (
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்பப் பெயர்:</strong></span>
                  <span>{familyData.family.familyName}</span>
                </div>
              )}
              {familyData.family.familyOrigin && (
                <div className="detail-item">
                  <span className="detail-label"><strong>குடும்ப பூர்வீக ஊர்:</strong></span>
                  <span>{familyData.family.familyOrigin}</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label"><strong>நிலை:</strong></span>

                <span style={{ color: ['draft', 'inactive', 'pending'].includes(familyData.family.status) ? 'red' : 'inherit' }}>
                  {familyData.family.status || ""}
                </span>
                <div>
                  <small>{statusExplanation[familyData.family.status] || "இது ஒரு அறியப்படாத நிலை."}</small>
                </div>

              </div>
              {
                familyData.family.keyContact && (
                  <div className="detail-item">
                    <span className="detail-label"><strong>முக்கிய தொடர்பு:</strong> </span>
                    <span className="detail-value">
                      {familyData.family.keyContact ? '✅' : '❌'}
                    </span>
                  </div>
                )
              }
              {
                familyData.family.pongalTaxUptoYear && (
                  <div className="detail-item">
                    <span className="detail-label"><strong>பொங்கல் வரி:</strong> </span>
                    <span className="detail-value">
                      {familyData.family.pongalTaxUptoYear}
                    </span>
                  </div>
                )
              }
              {
                familyData.family.chinnaKovil2025TempleConstructionTotal && (
                  <div className="detail-item">
                    <span className="detail-label"><strong>திருப்பணி:</strong> </span>
                    <span className="detail-value">
                      {familyData.family.chinnaKovil2025TempleConstructionTotal}
                    </span>
                  </div>
                )
              }
              {familyData?.family?.addressData?.tempAddress?.pincode && (
                <>
                  <hr></hr>
                  <div className="detail-item">
                    <span className="detail-label"><strong>தற்காலிக முகவரி :</strong></span>
                    <div>
                      {/* Check for tempAddressLine1, tempAddressLine2, tempAddressLine3 */}
                      <span>
                        {familyData?.family?.tempAddressLine1 && familyData.family.tempAddressLine1 + ','}
                        {familyData?.family?.tempAddressLine2 && familyData.family.tempAddressLine2 + ','}
                        {familyData?.family?.tempAddressLine3 && familyData.family.tempAddressLine3}
                      </span>
                      <br />
                      {/* Check for address data (name, block, district, state, country) */}
                      <span>
                        {familyData?.family?.addressData?.tempAddress?.subAreas[0].name
                          .split("|")
                          .reverse()
                          .map((token) => token + ",")}
                      </span>
                      <br />
                      <span>
                        {familyData?.family?.addressData?.tempAddress?.name && familyData.family.addressData.tempAddress.name + ', '}
                        {familyData?.family?.addressData?.tempAddress?.block && familyData.family.addressData.tempAddress.block + ', '}
                        {familyData?.family?.addressData?.tempAddress?.district && familyData.family.addressData.tempAddress.district + ', '}
                        {familyData?.family?.addressData?.tempAddress?.state && familyData.family.addressData.tempAddress.state + ', '}
                        {familyData?.family?.addressData?.tempAddress?.country && familyData.family.addressData.tempAddress.country}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label"><strong></strong></span>
                    <div>
                      {/* Check for Tamil address lines */}
                      <span>
                        {familyData?.family?.tempAddressLine1_tamil && familyData.family.tempAddressLine1_tamil + ','}
                        {familyData?.family?.tempAddressLine2_tamil && familyData.family.tempAddressLine2_tamil + ','}
                        {familyData?.family?.tempAddressLine3_tamil && familyData.family.tempAddressLine3_tamil}

                      </span>
                      <br />
                      {/* Check for Tamil address data */}
                      <span>
                        {familyData?.family?.addressData?.tempAddress?.subAreas[0].name_tamil
                          .split("|")
                          .reverse()
                          .map((token) => token + ",")}
                      </span>
                      <br />
                      <span>
                        {familyData?.family?.addressData?.tempAddress?.name_tamil && familyData.family.addressData.tempAddress.name_tamil + ', '}
                        {familyData?.family?.addressData?.tempAddress?.block_tamil && familyData.family.addressData.tempAddress.block_tamil + ', '}
                        {familyData?.family?.addressData?.tempAddress?.district_tamil && familyData.family.addressData.tempAddress.district_tamil + ', '}
                        {familyData?.family?.addressData?.tempAddress?.state_tamil && familyData.family.addressData.tempAddress.state_tamil + ', '}
                        {familyData?.family?.addressData?.tempAddress?.country_tamil && familyData.family.addressData.tempAddress.country_tamil}
                      </span>

                      {/* Check for pincode */}
                      <span>
                        <br />
                        {familyData?.family?.addressData?.tempAddress?.pincode && familyData.family.addressData.tempAddress.pincode}
                      </span>
                    </div>
                  </div>
                </>
              )}

              <hr />

              <div className="detail-item">
                <span className="detail-label"><strong>நிரந்தர முகவரி:</strong></span>
                <div>
                  {/* Check for permanentAddressLine1, permanentAddressLine2, permanentAddressLine3 */}
                  <span>
                    {familyData?.family?.permanentAddressLine1 && familyData.family.permanentAddressLine1 + ','}
                    {familyData?.family?.permanentAddressLine2 && familyData.family.permanentAddressLine2 + ','}
                    {familyData?.family?.permanentAddressLine3 && familyData.family.permanentAddressLine3}
                  </span>
                  <br />
                  {/* Check for address data (name, block, district, state, country) */}
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.subAreas[0].name
                      .split("|")
                      .reverse()
                      .map((token) => token + ",")}
                  </span>
                  <br />
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.name && familyData.family.addressData.permanentAddress.name + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.block && familyData.family.addressData.permanentAddress.block + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.district && familyData.family.addressData.permanentAddress.district + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.state && familyData.family.addressData.permanentAddress.state + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.country && familyData.family.addressData.permanentAddress.country}

                  </span>
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label"><strong></strong></span>
                <div>
                  <span>
                    {familyData?.family?.permanentAddressLine1_tamil && familyData.family.permanentAddressLine1_tamil + ','}
                    {familyData?.family?.permanentAddressLine2_tamil && familyData.family.permanentAddressLine2_tamil + ','}
                    {familyData?.family?.permanentAddressLine3_tamil && familyData.family.permanentAddressLine3_tamil}
                  </span>
                  <br />
                  {/* Check for Tamil permanent address data */}
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.subAreas[0].name_tamil
                      .split("|")
                      .reverse()
                      .map((token) => token + ",")}
                  </span>
                  <br />
                  <span>

                    {familyData?.family?.addressData?.permanentAddress?.name_tamil && familyData.family.addressData.permanentAddress.name_tamil + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.block_tamil && familyData.family.addressData.permanentAddress.block_tamil + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.district_tamil && familyData.family.addressData.permanentAddress.district_tamil + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.state_tamil && familyData.family.addressData.permanentAddress.state_tamil + ', '}
                    {familyData?.family?.addressData?.permanentAddress?.country_tamil && familyData.family.addressData.permanentAddress.country_tamil}
                  </span>

                  {/* Check for pincode */}
                  <span>
                    <br />
                    {familyData?.family?.addressData?.permanentAddress?.pincode && familyData.family.addressData.permanentAddress.pincode}
                  </span>
                </div>
              </div>

            </div>
            <div  >
              <table >
                <tbody>
                  <tr>
                    <td style={{ border: "none" }} colSpan={2}>
                      {(
                        (typeof id === 'string' && id.startsWith("TMP"))
                      ) && (
                          <button type="button" onClick={() => navigate(`/family/activate/${id}`)} style={{ backgroundColor: 'red' }}>
                            செயல்படுத்தவும் (Activate Pending Family)
                          </button>
                        )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "none" }}>
                      <button onClick={() => navigate(`/family/member-create/${id}`)}><FaUserPlus size={20} />உறுப்பினர் சேர் </button>
                    </td>
                    <td style={{ border: "none", gap: "10px", justifyContent: "center" }}>
                      <button onClick={() => navigate(`/family/update/${id}`)}><FaEdit size={20} /> குடும்பத்தை புதுப்பி</button>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "none", gap: "10px", justifyContent: "center" }}>
                      <button onClick={() => navigate(`/family`)}><FaUsers size={20} /> குடும்ப நிர்வாகம்</button>
                    </td>
                    <td style={{ border: "none", gap: "10px", justifyContent: "center" }}>
                      <button onClick={() => navigate(`/family/address-update/${id}`)}><FaMapMarkerAlt size={20} /> முகவரியை புதுப்பி</button>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ border: "none" }} colSpan={2}>
                      {(
                        (user?.roles?.includes('FamilyAdmin'))
                      ) && (
                          <>
                            <button type="button" onClick={() => navigate(`/family/member-bulk-move/${id}`)}>
                              <FaExchangeAlt size={20} />குடும்ப உறுப்பினர் கூட்டமாக மாற்று (Bulk Move)
                            </button>
                            <button onClick={loadAllMemberPhotos}>அனைத்து உறுப்பினர் புகைப்படங்களையும் ஏற்று</button>
                          </>
                        )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div >
      <br />
      {/* குடும்ப உறுப்பினர்கள் Table */}
      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <h3>குடும்ப உறுப்பினர்கள்</h3>
            <div>

              {renderMemberTree(buildMemberHierarchy(familyData.family))}
            </div>
            <br />
            <div className="table-container">
              <table className="table" aria-label="Recent Families">
                <thead>
                  <tr>
                    {showPhotoColumn && <th>புகைப்படம்</th>}
                    <th>விவரங்கள்</th>
                    <th>செயல்கள்</th>
                  </tr>
                </thead>
                <tbody>
                  {familyData.family.familyMembers.map((member, index) => {
                    const isSelf = familyData.family.familyHeadAadhar === member.aadhar;  // Check if parentId and aadhar are the same
                    // Find the parent member based on parentId (assuming parentId matches with member's aadhar or id)
                    const parent = familyData.family.familyMembers.find((parentMember) => parentMember.aadhar === member.parentId);
                    const parentFirstName = parent ? parent.firstName : ""; // Use parent's first name if available
                    return (
                      <tr key={index}>
                        {showPhotoColumn && (
                          <td>
                            <div className="form-image">
                              <MemberPhoto memberUid={member.uid} loadedPhotoImages={loadedPhotoImages} />
                            </div>
                          </td>
                        )}
                        <td>
                          {member.firstName || ""} {member.lastName || ""}
                          {isSelf
                            ? " (👑)"
                            : ` (${parentFirstName || maskAadhar(member.maskedParentId) || ""}-இன் ${member.relationship || ""})`
                          }
                          <br />

                          <span style={{ color: member.maskedAadhar?.startsWith('TMP') ? 'red' : 'inherit' }}>
                            🪪 {maskAadhar(member.maskedAadhar) || ""}
                          </span>
                          <br />
                          <span style={{ color: ['draft', 'inactive'].includes(member.status) ? 'red' : 'inherit' }}>
                            🚦 {member.status || ""} {statusExplanation[member.status] || "இது ஒரு அறியப்படாத நிலை."}
                          </span>
                          <br />
                          {member.mobile && (
                            <>
                              📱 {member.mobile}
                              {member.mobile2 && ` / ${member.mobile2}`}
                              <br />
                            </>
                          )}
                          {member.email && (
                            <>
                              📩 {member.email}
                              <br />
                            </>
                          )}
                          🔄 {convertTimestampToDate(member.updatedAt).toLocaleString()}
                        </td>

                        <td>
                          <button type="button" onClick={() => navigate(`/family/member-view/${member.id}`)}>
                            👁️
                          </button>
                          <br />
                          <button type="button" onClick={() => navigate(`/family/member-update/${member.id}`)}>
                            ✏️
                          </button>
                          <br />
                          {typeof member.id === 'string' && (member.maskedAadhar?.startsWith("TMP") || member.maskedAadhar?.startsWith("00")) && (
                            <button type="button" onClick={() => navigate(`/family/member-activate/${member.id}`)} style={{ backgroundColor: 'red' }}>
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

              </table>
            </div>
          </div>
        </div>
      </div>
      <br />
      {/* Application Form List */}
      {
        user?.roles?.includes('FamilyAdmin') && (
          <div className="general_parent">
            <div className="general_container">
              <div className="general_body">
                <h3>விண்ணப்பப் படிவம்</h3>
                <button onClick={loadLatestApplicationForm}>சமீபத்திய விண்ணப்பப் படிவத்தை ஏற்றவும்</button>
                <button onClick={loadAllApplicationForms}>அனைத்து விண்ணப்பப் படிவங்களையும் ஏற்று</button>
                <br />

                {loadingApplicationForm && <Loading />}

                {/* Latest application form */}
                {applicationForm ? (
                  <div>
                    <strong>சமீபத்திய விண்ணப்பம்:</strong><br />
                    <img
                      src={applicationForm.signedUrl}
                      alt="Application form"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                    <br /><br />
                  </div>
                ) : (
                  <div>சமீபத்திய விண்ணப்பப் படிவம் இன்னும் ஏற்றப்படவில்லை.</div>
                )}

                {/* All application forms */}
                {Array.isArray(loadedApplicationImages) && loadedApplicationImages.length > 0 ? (
                  <div>
                    <strong>அனைத்து விண்ணப்பங்கள்:</strong>
                    {loadedApplicationImages.map((form, index) => (
                      <div key={index}>
                        <br />
                        <small>புதுப்பிக்கப்பட்ட தேதி: {new Date(form.updatedAt._seconds * 1000).toLocaleString()}</small>
                        <img
                          src={form.signedUrl}
                          alt="Application form"
                          style={{ maxWidth: '100%', height: 'auto' }}
                        />
                        <br /><br />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>அனைத்து விண்ணப்பப் படிவம் இன்னும் ஏற்றப்படவில்லை.</div>
                )}
              </div>
            </div>
          </div>
        )
      }
      <Footer />
    </>
  );
}
MemberPhoto.propTypes = {
  memberUid: PropTypes.string.isRequired,
  loadedPhotoImages: PropTypes.array.isRequired,
};
export default FamilyView;